"use client";

import { useState, useEffect } from "react";
import { useAppSelector } from "@/hooks/hooks";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { MapPin, Clock, ChevronRight, ChevronLeft } from "lucide-react";
import Link from "next/link";
import Slider from "react-slick";
import "../styles/carousel.css";

function NextArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Next slide"
    >
      <ChevronRight className="w-5 h-5" />
    </button>
  );
}

function PrevArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Previous slide"
    >
      <ChevronLeft className="w-5 h-5" />
    </button>
  );
}

export default function NearbyAttractions({ cmsData }: { cmsData?: any }) {
  const { homeDetails } = useAppSelector((state) => state.home);
  const apiAttractions = homeDetails?.Atraction ?? [];

  // Compute slidesToShow from actual window width (fixes SSR + mobile issue)
  const getSlidesToShow = () => {
    if (typeof window === "undefined") return 3;
    const w = window.innerWidth;
    if (w <= 640) return 1;
    if (w <= 1024) return 2;
    return 3;
  };

  const [slidesCount, setSlidesCount] = useState(getSlidesToShow);

  useEffect(() => {
    const onResize = () => setSlidesCount(getSlidesToShow());
    window.addEventListener("resize", onResize);
    onResize(); // apply immediately on mount
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (apiAttractions.length === 0) {
    return null;
  }

  const getCleanText = (htmlContent: string) => {
    if (!htmlContent) return "";
    return htmlContent
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, " ")
      .replace(/\s+([.,!?;:])/g, "$1")
      .trim();
  };

  const displayAttractions = apiAttractions.map((attr: any) => ({
    name: attr.Title || "",
    distance: attr.Distance || "",
    time: attr.TravelTime || "",
    description: getCleanText(attr.Description || ""),
    image: attr.MainImage || attr.BannerImage || "/placeholder.png",
    url: attr.AttractionURL || "",
  }));

  const showArrows = slidesCount > 1 && displayAttractions.length > 1;

  const sliderSettings = {
    dots: true,
    infinite: displayAttractions.length > slidesCount,
    speed: 500,
    slidesToShow: slidesCount,
    slidesToScroll: 1,
    autoplay: displayAttractions.length > slidesCount,
    autoplaySpeed: 5000,
    swipe: true,
    touchMove: true,
    arrows: showArrows,
    nextArrow: showArrows ? <NextArrow /> : undefined,
    prevArrow: showArrows ? <PrevArrow /> : undefined,
  };

  // Card component reused in both slider and grid
  const AttractionCard = ({ attraction }: { attraction: any }) => (
    <div className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 flex flex-col justify-between h-full min-h-[400px]">
      {/* Top Section */}
      <div>
        {/* Image */}
        {attraction.image && (
          <div className="relative h-48 overflow-hidden">
            <ImageWithFallback
              src={attraction.image}
              alt={attraction.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

            {/* Distance Badge */}
            {attraction.distance && (
              <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-2 shadow-sm z-10">
                <MapPin className="w-4 h-4 text-pink-600" />
                <span className="text-sm font-sans font-medium text-gray-800">
                  {attraction.distance}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          <h3 className="text-xl font-sans font-semibold text-gray-800 mb-2 group-hover:text-pink-600 transition duration-300">
            {attraction.name}
          </h3>

          {/* Travel Time */}
          {attraction.time && (
            <div className="flex items-center gap-2 text-gray-500 mb-3">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-sans font-normal">
                {attraction.time} from tent city
              </span>
            </div>
          )}

          {/* Description */}
          {attraction.description && (
            <p className="text-gray-600 text-sm font-sans font-normal leading-relaxed mb-2 line-clamp-3">
              {attraction.description}
            </p>
          )}
        </div>
      </div>

      {/* Action Button */}
      {attraction.url && (
        <div className="px-6 pb-6">
          <Link
            href={`/travel-guide/${attraction.url}`}
            className="text-pink-600 hover:text-pink-700 transition cursor-pointer font-sans font-medium text-sm inline-flex items-center gap-1 group/btn"
          >
            Learn More
            <span className="group-hover/btn:translate-x-1 transition-transform">
              →
            </span>
          </Link>
        </div>
      )}
    </div>
  );

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        {cmsData?.Template ? (
          <div
            dangerouslySetInnerHTML={{ __html: cmsData.Template }}
            className="text-center mb-12"
          />
        ) : (
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl mb-4 font-sans font-semibold text-gray-900">
              Nearby{" "}
              <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 bg-clip-text text-transparent">
                Attractions
              </span>
            </h2>
            <p className="text-xl text-gray-600 font-sans font-normal">
              Explore the Rich Heritage of Kutch
            </p>
          </div>
        )}

        {displayAttractions.length > 1 ? (
          <div className="relative px-0 sm:px-8 select-text overflow-visible">
            <Slider {...sliderSettings}>
              {displayAttractions.map((attraction: any, index: number) => (
                <div key={index} className="px-1 sm:px-3 pb-4">
                  <AttractionCard attraction={attraction} />
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayAttractions.map((attraction: any, index: number) => (
              <AttractionCard key={index} attraction={attraction} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
